import React, { useState } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import PDFViewer from "../../modules/shared/component/pdf/PDFViewer";
import SearchInput from "../../modules/shared/component/SearchInput";
import OrganizationProfilesList from "../../modules/organisasi_internasional/component/OrganizationProfilesList";
import OrganizationProfilesSearch from "../../modules/organisasi_internasional/component/OrganizationProfilesSearch";
import useQueryPdfData from "../../modules/shared/hooks/useQueryPdfData";

// Main list page component with search functionality
const OrganizationProfilesListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  return (
    <div>
      <div className="mx-auto w-11/12 laptop:max-w-full desktop:max-w-7xl">
        <div className="flex flex-row my-12">
          <div className="p-4 w-full">
            <div className="card w-full bg-neutral text-neutral-content shadow-xl border-t-8 border-green-600 rounded-none">
              <div className="card-body p-0">
                <div className="flex flex-row px-6 pt-6">
                  <div className="grow">
                    <h2 className="card-title text-2xl font-bold">
                      Organisasi Internasional
                    </h2>
                  </div>
                  <div className="shrink">
                    <div className="text-sm breadcrumbs">
                      <ul>
                        <li>Tentang</li>
                        <li>
                          <a href="/tentang/organisasi-internasional">
                            Organisasi Internasional
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="divider m-0"></div>

                <div className="mb-8">
                  <div className="mx-12 mb-8">
                    <p className="text-lg mb-6">
                      Daftar organisasi internasional dari berbagai negara dan
                      institusi, dikelompokkan berdasarkan negara untuk
                      memudahkan pencarian.
                    </p>

                    {/* Search Input */}
                    <div className="mb-8">
                      <SearchInput
                        placeholder="Cari organisasi berdasarkan negara, kategori, atau nama organisasi..."
                        onSearch={handleSearch}
                        className="max-w-2xl"
                      />
                    </div>

                    {/* Content */}
                    <div className="mt-8">
                      {searchTerm.trim() ? (
                        <OrganizationProfilesSearch searchTerm={searchTerm} />
                      ) : (
                        <OrganizationProfilesList />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PDF Viewer component that displays a specific PDF using the new API
const OrganizationProfilesViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQueryPdfData(
    id ? parseInt(id) : null,
  );

  const handleBack = () => {
    navigate("/tentang/organisasi-internasional");
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="loading loading-spinner loading-lg text-green-500"></div>
      </div>
    );
  }

  // Error state
  if (
    isError ||
    !id ||
    (data && !data.data) ||
    (data &&
      data.data &&
      (!Array.isArray(data.data) || data.data.length === 0)) ||
    (data &&
      data.data &&
      Array.isArray(data.data) &&
      data.data[0] &&
      !data.data[0].url)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
        <div className="card w-full max-w-md bg-base-100 shadow-2xl">
          <div className="card-body text-center">
            <div className="mx-auto w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-error"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-base-content mb-2">
              Dokumen Tidak Ditemukan
            </h1>

            <p className="text-base-content/70 mb-6">
              Dokumen yang Anda cari tidak dapat ditemukan atau telah dihapus.
              Silakan periksa kembali atau coba dokumen lainnya.
            </p>

            <div className="card-actions justify-center">
              <button onClick={handleBack} className="btn btn-success gap-x-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Kembali ke Daftar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Return early if data is not yet loaded
  if (!data) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="loading loading-spinner loading-lg text-green-500"></div>
      </div>
    );
  }

  const documentData = data.data[0]; // Access first element of the array
  const formattedDate = dayjs(documentData.created_at).format("DD MMMM YYYY");

  return (
    <div className="min-h-screen relative z-10 flex flex-col">
      <PDFViewer
        pdfUrl={documentData.url}
        onBack={handleBack}
        title={documentData.judul}
        date={formattedDate}
        breadcrumbs={[
          {
            label: "Organisasi Internasional",
            link: "/tentang/organisasi-internasional",
          },
          {
            label: documentData.judul,
            link: `/tentang/organisasi-internasional/view/${documentData.id}`,
          },
        ]}
      />
    </div>
  );
};

// Main component that handles routing
const OrganisasiInternasional: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<OrganizationProfilesListPage />} />
      <Route path="/view/:id" element={<OrganizationProfilesViewerPage />} />
    </Routes>
  );
};

export default OrganisasiInternasional;
